// istanbul ignore file
import React from 'react'
import { Grid, TextField, Typography, FormControlLabel, Checkbox, Card, CardContent } from '@mui/material'
import { type Stage } from '../create-custom-kpi/types'
import { GenXDropdown, type Option } from '../../../../components/GenXDropdown'
import ParameterForm from './ParameterForm'
import LanguageStrings from '../../../../i18n/locales'
import InfoVector from '../../../../assets/InfoVector.png'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import {
  setStages, setTrueCondition, setScaleFactor, setTotalizerType, setTotalizePeriod
} from '../../redux/customKpiSlice'
import {
  useSelector,
  useDispatch
} from 'react-redux'
import { type RootState } from '../../../../store'

const customKpi = LanguageStrings().ThermalIQStrings.CreateCustomKpiComponent
interface StageFormProps {
  index: number
  isTotalizer: boolean
  stages: Stage[]
  assetList: any
  arithmeticIndicators: Option[]
  logicalIndicators: Option[]
  operators: Option[]
  operatorType: string
  handleStageChange: (index: number, field: keyof Stage, value: Option | string, asset?: Option | null, device?: Option | null) => void
  removeStage?: () => void
  kpiType: string
  deviceType: string
  currentKpiId?: string
}

const StageForm: React.FC<StageFormProps> = ({
  index,
  isTotalizer,
  stages,
  assetList,
  operators,
  arithmeticIndicators,
  logicalIndicators,
  operatorType,
  handleStageChange,
  removeStage,
  kpiType,
  deviceType,
  currentKpiId
}) => {
  const customKpiState = useSelector((state: RootState) => state.customKpi)
  const isCalculatedParameter = index > 0
  const arithmeticOperator = index === 0 ? [...arithmeticIndicators] : [{ value: '', label: 'None' }, ...arithmeticIndicators]
  const logicalOperator = index === 0 ? [...logicalIndicators] : [{ value: '', label: 'None' }, ...logicalIndicators]
  const [selectedOperator, setSelectedOperator] = React.useState<Option | null>(null)
  const [selectedOperation, setSelectedOperation] = React.useState('')
  const [isTrueCondition, setIsTrueCondition] = React.useState<boolean>(customKpiState.trueCondition)
  const [scaleValue, setScaleValue] = React.useState<any>(customKpiState.scaleFactor)
  const [scaleErrorInit, setScaleErrorInit] = React.useState(false)
  const [selectedPeriod, setSelectedPeriod] = React.useState<Option | null>(null)
  const [selectedType, setSelectedType] = React.useState<Option | null>(null)
  const dispatch = useDispatch()

  const textStyle = {
    root: {
      backgroundColor: '#272727',
      '& .MuiInputBase-input': {
        color: '#F0F0F0',
        backgroundColor: '#404040'
      },
      '& .MuiInputLabel-root': {
        color: '#F0F0F0',
        '&.Mui-focused': {
          color: '#F0F0F0'
        }
      },
      '& .MuiOutlinedInput-root': {
        '& fieldset': {
          borderColor: '#00000000'
        },
        '&:hover fieldset': {
          borderColor: '#00000000'
        },
        '&.Mui-focused fieldset': {
          borderColor: '#00000000'
        }
      }
    }
  }

  React.useEffect(() => {
    setIsTrueCondition(customKpiState.trueCondition)
  }, [customKpiState.trueCondition])

  React.useEffect(() => {
    setSelectedOperator(operatorType === '' ? null : operatorType === 'Logical Indicator' ? { value: 'LOGICAL', label: 'Logical' } : { value: 'ARITHMETIC', label: 'Arithmetic' })
  }, [operatorType])

  React.useEffect(() => {
    setScaleValue(customKpiState.scaleFactor)
    const typeOption = typeOptions.filter(item => item.value === customKpiState.totalizerType)
    setSelectedType(typeOption.length > 0 ? typeOption[0] : null)
    const periodOption = periodOptions.filter(item => item.value === customKpiState.totalizePeriod)
    setSelectedPeriod(periodOption.length > 0 ? periodOption[0] : null)
  }, [customKpiState.totalizerType, customKpiState.totalizePeriod])

  const handleSelectOperator = (selectedValue: Option): void => {
    setSelectedOperation('')
    setSelectedOperator(selectedValue)
    handleOperatorChange(selectedValue.value)
  }
  const handleOperatorChange = (operator: string): void => {
    const updatedStages = stages.map((stage, stageIndex) =>
      index === stageIndex
        ? {
            ...stage, operatorType: operator, arithmeticIndicator: { value: '', label: selectOperatorPlaceholder(operator) }
          }
        : stage
    )
    dispatch(setStages(updatedStages))
  }
  const selectOperatorPlaceholder = (operator: string): string => {
    if (operator === 'LOGICAL') {
      return 'Select Logical Operator'
    } else if (operator === 'ARITHMETIC') {
      return 'Select Arithmetic Operator'
    } else {
      return 'Select Operator'
    }
  }
  const noneOperator: Option[] = [
    { value: '', label: 'None' }
  ]
  const handleOperatorOption = (): Option[] => {
    if (stages[index].operatorType === 'LOGICAL') {
      return logicalOperator
    } else if (stages[index].operatorType === 'ARITHMETIC') {
      return arithmeticOperator
    } else {
      return noneOperator
    }
  }
  const selectPlaceholder = (): string => {
    if (stages[index].operatorType === 'LOGICAL') {
      return 'Select Logical Operator'
    } else if (stages[index].operatorType === 'ARITHMETIC') {
      return 'Select Arithmetic Operator'
    } else {
      return 'Select Operator'
    }
  }

  const handleOperationChange = (index: number, selectedValue: any): void => {
    setSelectedOperation(selectedValue.value)
    handleStageChange(index, 'arithmeticIndicator', selectedValue)
  }
  const handlePeriodChange = (selectedValue: Option): void => {
    setSelectedPeriod(selectedValue)
    dispatch(setTotalizePeriod(selectedValue.value))
  }
  const handleTypeChange = (selectedValue: Option): void => {
    setSelectedType(selectedValue)
    dispatch(setTotalizerType(selectedValue.value))
  }
  const handleScaleFactor = (value: string): void => {
    setScaleValue(value)
    setScaleErrorInit(true)
    dispatch(setScaleFactor(value))
  }
  const handleTrueConditionChange = (e: any): void => {
    setIsTrueCondition(e.target.checked)
    dispatch(setTrueCondition(e.target.checked))
  }

  const isValidScale = (value: string): boolean => {
    const regexValidator = /^(?:0|[1-9]\d*)(?:\.\d+)?$/
    return regexValidator.test(value)
  }
  const isScaleNotValid = (): boolean => {
    return (!isValidScale(scaleValue))
  }
  const typeOptions: Option[] = [
    { value: 'ACCUMULATOR', label: 'Accumulator' },
    { value: 'RATE', label: 'Rate' },
    { value: 'RISING_EDGE', label: 'Rising Edge' },
    { value: 'FALLING_EDGE', label: 'Falling Edge' },
    { value: 'DURATION', label: 'Duration' }
  ]
  const periodOptions: Option[] = [
    { value: 'TODAY', label: 'Today' },
    { value: 'YESTERDAY', label: 'Yesterday' },
    { value: 'THIS_HOUR', label: 'This Hour' },
    { value: 'LAST_HOUR', label: 'Last Hour' },
    { value: 'LAST_24_HOURS', label: 'Last 24 Hours' },
    { value: 'THIS_WEEK', label: 'This Week' },
    { value: 'LAST_WEEK', label: 'Last Week' },
    { value: 'THIS_MONTH', label: 'This Month' },
    { value: 'LAST_MONTH', label: 'Last Month' }
  ]

  return (
    <>
    <Grid data-testid={`stage-form-${index}`} container spacing={4} style={{ display: 'flex', flexDirection: 'column', marginBottom: '20px' }}>
      {/* Show checkbox when totalizer enabled */}
      {index === 0 && isTotalizer && (
        <Grid data-testid="true-condition" item>
          <FormControlLabel
            control={
              <Checkbox checked={isTrueCondition} onChange={(e) => { handleTrueConditionChange(e) }} name="true_condition" color="primary"
                sx={{
                  '& .MuiSvgIcon-root': {
                    color: isTrueCondition ? 'primary.main' : '#505050'
                  }
                }}
              />
            }
            label={
              <Typography component="span">
                True Condition
                <Tooltip title="Please provide a tag when the totalizer should function for the tags true status" placement="right" arrow>
                  <IconButton size="small">
                    <img style={{ width: '12px', height: '12px', marginLeft: '5px' }} src={InfoVector} />
                  </IconButton>
                </Tooltip>
              </Typography>
            }
          />
        </Grid>
      )}
      {/* Parameter 1 form or calculated parameter text box */}
      <Grid data-testid="parameter1-input" item>
        { (!isTotalizer && isCalculatedParameter)
          ? (
            <div>
              <span style={{ fontSize: '10px', display: 'block', color: '#F0F0F0' }}>
                <Typography sx={{ fontFamily: 'Honeywell Sans Web', fontSize: '14px', letterSpacing: '0.5px' }}>{customKpi.CalculatedParameter}</Typography>
              </span>
              <TextField
                value={stages[index - 1].calculatedName}
                size='small'
                placeholder='Enter Calc. Parameter Name'
                onChange={(event) => { handleStageChange(index - 1, 'calculatedName', event.target.value) }}
                style={{
                  fontSize: '11px',
                  width: '235px',
                  backgroundColor: '#353535'
                }}
                InputProps={{
                  style: {
                    color: '#B0B0B0'
                  }
                }}
              />
              <TextField
                size='medium'
                style={{
                  fontSize: '11px',
                  width: '235px',
                  backgroundColor: '#353535'
                }}
                InputProps={{
                  style: {
                    color: '#B0B0B0'
                  }
                }}
              />
            </div>
            )
          : null
        }
        { ((index === 0 && isTrueCondition && isTotalizer) || (index === 0 && !isTotalizer)) && (
          <ParameterForm
            key={`parameter1-form-${index}`}
            assetList={assetList}
            index={index}
            fieldKey={'parameter1'}
            parameter={ stages[index].parameter1 }
            handleStageChange={handleStageChange}
            kpiType={kpiType}
            deviceType={deviceType}
            isTotalizer={isTotalizer}
            currentKpiId={currentKpiId}
          />
        )}
      </Grid>
      {/* Parameter 2 form */}
      { (!isTotalizer || (index === 0 && isTotalizer)) && (
        <Grid data-testid="parameter2-input" item>
          <ParameterForm
            key={`parameter2-form-${index}`}
            assetList={assetList}
            index={index}
            fieldKey={'parameter2'}
            parameter={ stages[index].parameter2 }
            handleStageChange={handleStageChange}
            kpiType={kpiType}
            deviceType={deviceType}
            isTotalizer={isTotalizer}
            currentKpiId={currentKpiId}
          />
        </Grid>
      )}
    </Grid>
    { index === 0 && isTotalizer && (
      <Grid container style={{ padding: '0px 30px 0px 170px' }}>
        <div className="totalizer-content">
          <p className="totalizer-heading" style={{ fontFamily: 'Honeywell Sans Web', fontSize: '14px', fontStyle: 'normal' }}>Totalizer</p>
          <Card sx={{ backgroundColor: '#1A1A1A', overflow: 'visible', width: '350px', height: '300px', borderRadius: '10px', border: '2px solid #3B3B3B', color: '#F0F0F0 !important', padding: '2px' }}>
            <CardContent>
              <Grid container>
                <Grid item>
                  <Typography sx={{ fontFamily: 'Honeywell Sans Web', fontSize: '14px', padding: 0.5 }}>
                    Scale Factor <sup style={{ color: '#B0B0B0' }}> *</sup>
                  </Typography>
                  <TextField
                    id='scale-textfield'
                    variant="outlined"
                    placeholder='Enter Value'
                    InputLabelProps={{
                      disabled: true
                    }}
                    size='small'
                    className='custom-textfield'
                    value={scaleValue}
                    onChange={(e) => { handleScaleFactor(e.target.value) }}
                    sx={{
                      ...textStyle.root,
                      width: '300px',
                      border: (isScaleNotValid() && scaleErrorInit) ? '1px solid #EE3426' : 'none'
                    }}
                  />
                </Grid>
                <Grid item sx={{ marginTop: '12px', marginBottom: '12px' }}>
                  <span style={{ fontSize: '14px', display: 'block', color: '#F0F0F0' }}>
                    <Typography sx={{ fontFamily: 'Honeywell Sans Web', fontSize: '14px', letterSpacing: '0.5px' }}>
                      Tatalize period <sup style={{ color: '#B0B0B0' }}> *</sup>
                    </Typography>
                  </span>
                  <GenXDropdown disabled={true} options={periodOptions} value={selectedPeriod} onSelect={handlePeriodChange} placeholder='Select time period' width={300} />
                </Grid>
                <Grid item sx={{ marginTop: '12px' }}>
                  <span style={{ fontSize: '14px', display: 'block', color: '#F0F0F0' }}>
                    <Typography sx={{ fontFamily: 'Honeywell Sans Web', fontSize: '14px', letterSpacing: '0.5px' }}>
                      Type <sup style={{ color: '#B0B0B0' }}> *</sup>
                    </Typography>
                  </span>
                  <GenXDropdown disabled={true} options={typeOptions} value={selectedType} onSelect={handleTypeChange} placeholder='Select type' width={300} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </div>
      </Grid>
    )}
    { index === 0 && isTotalizer && (
      <Grid container style={{ alignItems: 'center', padding: '0 100px 0 110px' }}>
        <div>
          <span style={{ fontSize: '10px', display: 'block', color: '#F0F0F0' }}>
            <Typography sx={{ fontFamily: 'Honeywell Sans Web', fontSize: '14px', letterSpacing: '0.5px' }}>Output</Typography>
          </span>
          <TextField
            value={stages[index].calculatedName} size='small' placeholder='Select output datatype'
            onChange={(event) => { handleStageChange(index, 'calculatedName', event.target.value) }}
            sx={{
              ...textStyle.root,
              width: '300px'
            }}
            InputProps={{
              style: {
                color: '#B0B0B0'
              }
            }}
          />
        </div>
      </Grid>
    )}
    { !isTotalizer && (
      <Grid container style={{ alignItems: 'center', padding: '0 100px 0 100px' }}>
        <Grid direction='row' data-testid="arithmeticIndicator-input" item>
          <div style={{ display: 'flex' }}>
            {
              index >= 0
                ? (
                  <div style={{ marginTop: '-22px', marginRight: '60px' }}>
                    <Typography sx={{ fontFamily: 'Honeywell Sans Web', fontSize: '15px', letterSpacing: '0.5px' }}>
                      {customKpi.Operator}
                      {index === 0
                        ? <sup style={{ color: '#B0B0B0' }}> *</sup>
                        : null
                      }
                    </Typography>
                    <GenXDropdown
                      placeholder='Select Operator'
                      value={selectedOperator}
                      options={operators}
                      onSelect={(selectedValue) => { handleSelectOperator(selectedValue) }}
                      width={170}
                    />
                  </div>
                  )
                : null
            }
            <div style={{ position: 'relative' }}>
              {
                selectedOperation === 'AND' || selectedOperation === 'OR'
                  ? (
                      <Typography
                        sx={{
                          fontFamily: 'Honeywell Sans Web',
                          fontSize: '15px',
                          letterSpacing: '0.5px',
                          color: '#B0B0B0',
                          position: 'absolute',
                          top: '-50px',
                          left: '0'
                        }}
                      >
                        Ensure boolean inputs to avoid NaN output
                      </Typography>
                    )
                  : null
              }
              <GenXDropdown
                placeholder={selectPlaceholder()}
                value={stages[index].arithmeticIndicator.value !== '' ? stages[index].arithmeticIndicator : null}
                options={handleOperatorOption()}
                onSelect={(selectedValue) => { handleOperationChange(index, selectedValue) }}
                width={238}
              />
            </div>
          </div>
        </Grid>
      </Grid>
    )}
    </>
  )
}

export default StageForm
